import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { LiquidCard, CardContent } from '@/components/ui/liquid-glass-card';
import { computeRoi, type RoiInput } from '../lib/roi';
import { formatPLN, formatNumber } from '../lib/format';
import { useSeo } from '../lib/useSeo';

interface FieldProps {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
  step: number;
}

function Field({ label, value, onChange, min, max, step }: FieldProps) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-4">
        <label className="text-sm text-text-primary">{label}</label>
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          className="w-28 bg-surface border border-stroke rounded-lg px-3 py-1.5 text-sm text-text-primary text-right focus:outline-none focus:border-accent-to"
        />
      </div>
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="w-full accent-[hsl(var(--accent-to))]"
        aria-label={label}
      />
    </div>
  );
}

function Metric({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="border border-stroke rounded-2xl p-5 bg-bg/40">
      <p className="text-xs text-muted uppercase tracking-wide mb-1">{label}</p>
      <p className="text-2xl font-light text-text-primary">{value}</p>
      {sub && <p className="text-xs text-muted mt-1">{sub}</p>}
    </div>
  );
}

export default function RoiCalculatorPage() {
  const navigate = useNavigate();
  useSeo({
    title: 'Kalkulator ROI - DAIEN',
    description: 'Policz roczny zwrot z automatyzacji AI: oszczednosc czasu, prog rentownosci i koszt dodatkowych leadow.',
    path: '/kalkulator-roi',
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [v, setV] = useState<RoiInput>({
    investment: 5000,
    hourlyRate: 150,
    hoursPerWeek: 10,
    costPerLead: 80,
    extraLeadsPerMonth: 20,
  });
  const set = (key: keyof RoiInput) => (n: number) => setV((prev) => ({ ...prev, [key]: n }));
  const r = computeRoi(v);

  return (
    <div className="min-h-screen bg-bg text-text-primary">
      <header className="px-6 md:px-10 lg:px-16 pt-10 pb-6 max-w-6xl mx-auto">
        <Link to="/" className="text-sm text-muted hover:text-text-primary transition-colors inline-flex items-center gap-2">
          ← DAIEN
        </Link>
      </header>

      <section className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="mb-12"
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="w-8 h-px bg-stroke"></span>
            <span className="text-xs text-muted uppercase tracking-[0.3em]">Policz swoj zwrot</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light mb-4">
            Kalkulator <span className="font-display italic">ROI</span>
          </h1>
          <p className="text-muted max-w-2xl">
            Ustaw swoje liczby i zobacz roczny efekt automatyzacji: oszczedzony czas w zlotowkach,
            prog rentownosci i koszt pozyskania dodatkowych leadow.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Inputy */}
          <div className="bg-surface border border-stroke rounded-3xl p-8 space-y-7">
            <Field label="Poczatkowa inwestycja (zl)" value={v.investment} onChange={set('investment')} min={0} max={100000} step={500} />
            <Field label="Twoja stawka godzinowa (zl/h)" value={v.hourlyRate} onChange={set('hourlyRate')} min={0} max={1000} step={10} />
            <Field label="Godziny w tygodniu do zaoszczedzenia" value={v.hoursPerWeek} onChange={set('hoursPerWeek')} min={0} max={60} step={1} />
            <div className="h-px bg-stroke" />
            <Field label="Cena za leada (zl)" value={v.costPerLead} onChange={set('costPerLead')} min={0} max={1000} step={5} />
            <Field label="Liczba dodatkowych leadow / miesiac" value={v.extraLeadsPerMonth} onChange={set('extraLeadsPerMonth')} min={0} max={500} step={1} />
          </div>

          {/* Wynik */}
          <LiquidCard className="border-stroke">
            <CardContent className="p-8 flex flex-col h-full">
              <p className="text-xs text-muted uppercase tracking-[0.3em] mb-2">Roczny wynik netto</p>
              <p className="text-5xl md:text-6xl font-light text-text-primary mb-8">{formatPLN(r.netAnnual)}</p>
              <div className="grid grid-cols-2 gap-4">
                <Metric label="Oszczednosc czasu / rok" value={formatPLN(r.annualTimeSavings)} sub={`${formatNumber(r.annualHoursSaved)} h`} />
                <Metric label="ROI" value={`${formatNumber(r.roiPercent)}%`} />
                <Metric label="Prog rentownosci" value={r.paybackMonths !== null ? `${formatNumber(r.paybackMonths)} mies.` : 'natychmiast'} />
                <Metric label="Dodatkowe leady / rok" value={formatNumber(r.annualLeads)} sub={`koszt ${formatPLN(r.annualLeadCost)}`} />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <button
                  onClick={() => navigate('/quiz')}
                  className="inline-flex items-center justify-center gap-2 bg-text-primary text-bg px-6 py-3.5 rounded-full text-sm font-medium hover:scale-[1.02] transition-transform"
                >
                  Wypelnij quiz kwalifikacyjny
                  <ArrowRight className="w-4 h-4" />
                </button>
                <a
                  href="https://calendar.app.google/4V6jKtrVUtMihmoE9"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-stroke text-text-primary px-6 py-3.5 rounded-full text-sm font-medium hover:bg-bg transition-colors"
                >
                  Umow 20-min rozmowe
                </a>
              </div>
            </CardContent>
          </LiquidCard>
        </div>
      </section>
    </div>
  );
}
